import ArrowRightAlt from '@mui/icons-material/ArrowRightAlt';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  TreadAgentApiWriteType,
  TreadAgentSingleRecordWrite_Preview,
  TreadAgentSingleRecordWriteType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { startCase } from 'lodash-es';

import { TreadAgentToolCallPreview } from '~tread-agent/types';

function formatPreviewValue(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function SingleRecordWritePreviewContent({
  preview,
}: {
  preview: TreadAgentSingleRecordWrite_Preview;
}) {
  const theme = useTheme();
  const fieldNames = Array.from(
    new Set([...Object.keys(preview.before), ...Object.keys(preview.after)]),
  );
  const changedFieldNames = fieldNames.filter(
    (fieldName) =>
      JSON.stringify(preview.before[fieldName]) !==
      JSON.stringify(preview.after[fieldName]),
  );

  if (changedFieldNames.length === 0) {
    return (
      <Typography sx={{ color: theme.brandV2.colors.treadGray3, fontSize: '12px' }}>
        {t('tread_agent.confirmation.no_changes')}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.brandV2.colors.treadGray9,
        borderRadius: theme.brandV2.borderRadius,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.25,
      }}
    >
      {changedFieldNames.map((fieldName) => (
        <Box key={fieldName} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            sx={{
              color: theme.brandV2.colors.treadGray3,
              fontSize: '12px',
            }}
          >
            {startCase(fieldName)}
          </Typography>

          <Box
            sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 0.75 }}
          >
            <Typography
              sx={{
                color: theme.brandV2.colors.treadGray3,
                fontSize: '12px',
                textDecoration: 'line-through',
              }}
            >
              {formatPreviewValue(preview.before[fieldName])}
            </Typography>
            <ArrowRightAlt
              sx={{ color: theme.brandV2.colors.treadGray4, fontSize: 16 }}
            />
            <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
              {formatPreviewValue(preview.after[fieldName])}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function LabeledValuePanel({
  heading,
  values,
}: {
  heading: string;
  values: Record<string, unknown>;
}) {
  const theme = useTheme();
  const entries = Object.entries(values);

  if (entries.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.brandV2.colors.treadGray9,
        borderRadius: theme.brandV2.borderRadius,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        p: 1.25,
      }}
    >
      <Typography
        sx={{
          color: theme.brandV2.colors.treadGray3,
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {heading}
      </Typography>

      {entries.map(([fieldName, value]) => (
        <Box
          key={fieldName}
          sx={{ alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: 0.75 }}
        >
          <Typography sx={{ color: theme.brandV2.colors.treadGray3, fontSize: '12px' }}>
            {startCase(fieldName)}
          </Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
            {formatPreviewValue(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function ToolCallPreviewDetails({
  preview,
}: {
  preview: TreadAgentToolCallPreview | null;
}) {
  if (preview?.kind === TreadAgentSingleRecordWriteType.SINGLE_RECORD_WRITE) {
    return <SingleRecordWritePreviewContent preview={preview} />;
  }

  if (preview?.kind === TreadAgentApiWriteType.API_WRITE) {
    return (
      <LabeledValuePanel
        heading={t('tread_agent.confirmation.fields')}
        values={preview.body}
      />
    );
  }

  return null;
}
